import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IconArrowsExchange,
  IconCheck,
  IconLoader2,
  IconRefresh,
  IconSelector,
  IconX,
} from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

import api from "../lib/api";
import MainCard from "../components/MainCard";
import PageHeader from "../components/PageHeader";

interface TransferStore {
  id: number;
  external_id: string | null;
  name: string;
  level: number;
}

interface TransferBalance {
  storeId: number;
  storeName: string;
  debit: number;
  credit: number;
  balance: number;
  unit: "MB";
}

interface TransferResult {
  success: boolean;
  message: string;
  data: {
    fromStore: TransferStore;
    toStore: TransferStore;
    amount: number;
    description: string;
    previousBalance: number;
    newBalance: number;
    fromRewardId: number;
    toRewardId: number;
  };
}

const formatMb = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })} MB`;
};

// Session-level cache for the SPAR Mobile store list. The list is large
// (~thousands of rows) and rarely changes, so we keep it in memory for the
// lifetime of the SPA and reuse it across remounts and across both comboboxes.
const STORES_CACHE_TTL_MS = 30 * 60 * 1000;
let storesCache: { data: TransferStore[]; expiresAt: number } | null = null;
let storesInflight: Promise<TransferStore[]> | null = null;

async function fetchStoresCached(force = false): Promise<TransferStore[]> {
  const now = Date.now();
  if (!force && storesCache && now < storesCache.expiresAt) {
    return storesCache.data;
  }
  if (!force && storesInflight) {
    return storesInflight;
  }
  storesInflight = (async () => {
    try {
      const res = await api.get("/spar-mobile/transfer-promo-data/stores");
      const data: TransferStore[] = res.data?.data ?? [];
      storesCache = { data, expiresAt: Date.now() + STORES_CACHE_TTL_MS };
      return data;
    } finally {
      storesInflight = null;
    }
  })();
  return storesInflight;
}

const MAX_VISIBLE_STORES = 50;

interface StoreComboboxProps {
  label: string;
  stores: TransferStore[];
  selected: TransferStore | null;
  onSelect: (store: TransferStore | null) => void;
  loading: boolean;
  disabled?: boolean;
  excludeId?: number | null;
  placeholder?: string;
}

function StoreCombobox({
  label,
  stores,
  selected,
  onSelect,
  loading,
  disabled,
  excludeId,
  placeholder = "Select store...",
}: StoreComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce keystrokes so we re-filter at most every 120ms
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 120);
    return () => clearTimeout(handle);
  }, [search]);

  // Reset search when the popover closes so it's ready for next time
  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  const { visibleStores, totalMatches } = useMemo(() => {
    const needle = debouncedSearch.toLowerCase();
    const matches: TransferStore[] = [];
    for (const store of stores) {
      if (excludeId && store.id === excludeId) continue;
      if (needle) {
        const haystack = `${store.name} ${store.id} ${store.external_id ?? ""}`.toLowerCase();
        if (!haystack.includes(needle)) continue;
      }
      matches.push(store);
    }
    return {
      visibleStores: matches.slice(0, MAX_VISIBLE_STORES),
      totalMatches: matches.length,
    };
  }, [stores, excludeId, debouncedSearch]);

  const hiddenCount = Math.max(0, totalMatches - visibleStores.length);

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-lg border border-input bg-transparent px-3 text-sm transition-colors",
            "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
            "dark:bg-input/30",
            disabled && "cursor-not-allowed opacity-50",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate text-left">
            {selected
              ? `${selected.name} (id ${selected.id}${selected.external_id ? `, ext ${selected.external_id.trim()}` : ""})`
              : placeholder}
          </span>
          <IconSelector className="ml-2 size-4 shrink-0 text-muted-foreground" />
        </PopoverTrigger>
        <PopoverContent className="w-[--anchor-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name, id or external id..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {loading
                  ? "Loading stores..."
                  : stores.length === 0
                    ? "No stores available."
                    : "No matching store."}
              </CommandEmpty>
              {visibleStores.length > 0 && (
                <CommandGroup>
                  {visibleStores.map((store) => {
                    const isSelected = selected?.id === store.id;
                    return (
                      <CommandItem
                        key={store.id}
                        value={String(store.id)}
                        data-checked={isSelected}
                        onSelect={() => {
                          onSelect(isSelected ? null : store);
                          setOpen(false);
                        }}
                      >
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="truncate">{store.name}</span>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <Badge variant="outline" className="font-normal">
                              id {store.id}
                            </Badge>
                            <Badge
                              variant={
                                store.level === 1 ? "secondary" : "outline"
                              }
                              className="font-normal"
                            >
                              L{store.level}
                            </Badge>
                            {isSelected && (
                              <IconCheck className="size-4 text-primary" />
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {hiddenCount > 0 && (
                <div className="px-3 py-1.5 text-center text-xs text-muted-foreground">
                  Showing first {visibleStores.length} of {totalMatches} matches
                  — refine your search to see more
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function TransferPromoDataPage() {
  const [stores, setStores] = useState<TransferStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState("");

  const [fromStore, setFromStore] = useState<TransferStore | null>(null);
  const [toStore, setToStore] = useState<TransferStore | null>(null);

  const [balance, setBalance] = useState<TransferBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  const [amountInput, setAmountInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [lastResult, setLastResult] = useState<TransferResult["data"] | null>(
    null,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadStores = useCallback(async (force = false) => {
    setStoresLoading(true);
    setStoresError("");
    try {
      const data = await fetchStoresCached(force);
      setStores(data);
    } catch (err: any) {
      setStoresError(
        err.response?.data?.message || err.message || "Failed to load stores",
      );
      setStores([]);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  useEffect(() => {
    // Hydrate immediately from cache if available so the dropdown is ready
    // on first open without any network round-trip.
    if (storesCache && Date.now() < storesCache.expiresAt) {
      setStores(storesCache.data);
    }
    void loadStores(false);
  }, [loadStores]);

  const loadBalance = useCallback(async (storeId: number) => {
    setBalanceLoading(true);
    setBalanceError("");
    try {
      const res = await api.get(
        `/spar-mobile/transfer-promo-data/balance/${storeId}`,
      );
      setBalance(res.data?.data ?? null);
    } catch (err: any) {
      setBalanceError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load balance for source store",
      );
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fromStore) {
      setBalance(null);
      setBalanceError("");
      return;
    }
    void loadBalance(fromStore.id);
  }, [fromStore, loadBalance]);

  const parsedAmount = useMemo(() => {
    if (!amountInput.trim()) return null;
    const value = Number(amountInput);
    if (!Number.isFinite(value)) return null;
    return value;
  }, [amountInput]);

  const availableBalance = balance?.balance ?? null;

  const amountInvalidReason = useMemo(() => {
    if (parsedAmount === null) return "Enter an amount in MB";
    if (parsedAmount <= 0) return "Amount must be greater than 0";
    if (availableBalance === null) return "Source store balance unknown";
    if (parsedAmount > availableBalance)
      return `Amount cannot exceed available balance of ${formatMb(availableBalance)}`;
    return null;
  }, [parsedAmount, availableBalance]);

  const canSubmit =
    !!fromStore &&
    !!toStore &&
    fromStore.id !== toStore.id &&
    parsedAmount !== null &&
    parsedAmount > 0 &&
    availableBalance !== null &&
    parsedAmount <= availableBalance &&
    !submitting &&
    !balanceLoading;

  const handleSubmit = async () => {
    if (!fromStore || !toStore || parsedAmount === null) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await api.post<TransferResult>(
        "/spar-mobile/transfer-promo-data",
        {
          fromStoreId: fromStore.id,
          toStoreId: toStore.id,
          amountMb: parsedAmount,
        },
      );
      setLastResult(res.data?.data ?? null);
      setAmountInput("");
      setConfirmOpen(false);
      // Refresh source balance to reflect the deduction
      void loadBalance(fromStore.id);
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.message || err.message || "Transfer failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const previewDescription =
    fromStore && toStore && parsedAmount !== null && parsedAmount > 0
      ? `Transfer of ${parsedAmount}MB from ${fromStore.name} to ${toStore.name}`
      : null;

  return (
    <div>
      <PageHeader
        title="Transfer Promo Data"
        subtitle="Move SPAR Mobile promo data balance (in MB) between two stores by writing matching credit and debit rows in the rewards table."
      />

      {storesError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {storesError}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setStoresError("")}
            >
              <IconX className="size-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <MainCard className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Transfer Details</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadStores(true)}
            disabled={storesLoading}
          >
            <IconRefresh
              className={cn("size-4", storesLoading && "animate-spin")}
            />
            Refresh stores
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <StoreCombobox
            label="Store From (source)"
            stores={stores}
            selected={fromStore}
            onSelect={setFromStore}
            loading={storesLoading}
            excludeId={toStore?.id ?? null}
            placeholder="Select source store..."
          />
          <StoreCombobox
            label="Store To (destination)"
            stores={stores}
            selected={toStore}
            onSelect={setToStore}
            loading={storesLoading}
            excludeId={fromStore?.id ?? null}
            placeholder="Select destination store..."
          />
        </div>

        <Separator className="my-5" />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground">
              Available balance (Store From)
            </Label>
            <div className="mt-1.5 flex h-9 items-center gap-2 rounded-lg border border-input bg-muted/40 px-3 text-sm">
              {!fromStore && (
                <span className="text-muted-foreground">
                  Select a source store
                </span>
              )}
              {fromStore && balanceLoading && (
                <>
                  <IconLoader2 className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Loading...</span>
                </>
              )}
              {fromStore && !balanceLoading && balanceError && (
                <span className="text-destructive">{balanceError}</span>
              )}
              {fromStore &&
                !balanceLoading &&
                !balanceError &&
                balance && (
                  <span className="font-semibold">
                    {formatMb(balance.balance)}
                  </span>
                )}
            </div>
            {balance && (
              <p className="mt-1 text-xs text-muted-foreground">
                Debit: {formatMb(balance.debit)} · Credit:{" "}
                {formatMb(balance.credit)}
              </p>
            )}
          </div>

          <div className="md:col-span-1">
            <Label htmlFor="amount-input">Amount to transfer (MB)</Label>
            <Input
              id="amount-input"
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              placeholder="e.g. 1024"
              disabled={!fromStore || balanceLoading}
              className="mt-1.5"
            />
            {amountInvalidReason && parsedAmount !== null && (
              <p className="mt-1 text-xs text-destructive">
                {amountInvalidReason}
              </p>
            )}
            {!amountInvalidReason && availableBalance !== null && (
              <p className="mt-1 text-xs text-muted-foreground">
                Up to {formatMb(availableBalance)} available
              </p>
            )}
          </div>

          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground">
              New source balance (preview)
            </Label>
            <div className="mt-1.5 flex h-9 items-center gap-2 rounded-lg border border-input bg-muted/40 px-3 text-sm">
              {availableBalance !== null && parsedAmount !== null && parsedAmount > 0 ? (
                <span
                  className={cn(
                    "font-semibold",
                    parsedAmount > availableBalance && "text-destructive",
                  )}
                >
                  {formatMb(availableBalance - parsedAmount)}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </div>
        </div>

        {previewDescription && (
          <div className="mt-4 rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-3 text-xs">
            <span className="font-semibold text-muted-foreground">
              Reward description:
            </span>{" "}
            <span className="font-mono">{previewDescription}</span>
          </div>
        )}

        {submitError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription className="flex items-center justify-between">
              {submitError}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSubmitError("")}
              >
                <IconX className="size-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setFromStore(null);
              setToStore(null);
              setAmountInput("");
              setSubmitError("");
              setBalance(null);
              setBalanceError("");
            }}
            disabled={submitting}
          >
            <IconX className="size-4" />
            Reset
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit}
          >
            <IconArrowsExchange className="size-4" />
            Transfer Data
          </Button>
        </div>
      </MainCard>

      {lastResult && (
        <MainCard>
          <h3 className="mb-3 text-base font-semibold">Last Transfer</h3>
          <Separator className="mb-3" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailRow
              label="From"
              value={`${lastResult.fromStore.name} (id ${lastResult.fromStore.id})`}
            />
            <DetailRow
              label="To"
              value={`${lastResult.toStore.name} (id ${lastResult.toStore.id})`}
            />
            <DetailRow label="Amount" value={formatMb(lastResult.amount)} />
            <DetailRow
              label="Source balance change"
              value={`${formatMb(lastResult.previousBalance)} → ${formatMb(lastResult.newBalance)}`}
            />
            <DetailRow
              label="Source reward id"
              value={String(lastResult.fromRewardId)}
            />
            <DetailRow
              label="Destination reward id"
              value={String(lastResult.toRewardId)}
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Description written to both reward rows:
          </p>
          <p className="mt-1 rounded-md bg-muted px-2 py-1 font-mono text-xs">
            {lastResult.description}
          </p>
        </MainCard>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm transfer</DialogTitle>
            <DialogDescription>
              This will insert two rows into the rewards table and immediately
              move balance between stores.
            </DialogDescription>
          </DialogHeader>
          {fromStore && toStore && parsedAmount !== null && (
            <div className="space-y-2 text-sm">
              <DetailRow
                label="From"
                value={`${fromStore.name} (id ${fromStore.id})`}
              />
              <DetailRow
                label="To"
                value={`${toStore.name} (id ${toStore.id})`}
              />
              <DetailRow label="Amount" value={formatMb(parsedAmount)} />
              {balance && (
                <DetailRow
                  label="Source balance after"
                  value={`${formatMb(balance.balance)} → ${formatMb(balance.balance - parsedAmount)}`}
                />
              )}
              <div className="rounded-md bg-muted px-2 py-1.5 font-mono text-xs">
                Transfer of {parsedAmount}MB from {fromStore.name} to{" "}
                {toStore.name}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !canSubmit}>
              {submitting ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconArrowsExchange className="size-4" />
              )}
              {submitting ? "Transferring..." : "Confirm transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="min-w-[160px] text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export default TransferPromoDataPage;
